import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { type Student } from "@/services";
import {
  CheckCircle,
  TrendingUp,
  AlertCircle,

  Eye,

  Mail,
  Phone,
  Heart,
  Gift,
  Sparkles,
} from "lucide-react";

interface ChildCardProps {
  student: Student;
  onCargarSaldo: (student: Student) => void;
  onVerConsumos: (student: Student) => void;
  className?: string;
}

/**
 * Formatea el saldo como moneda chilena
 */
// const formatSaldo = (saldo: number): string => {
//   return new Intl.NumberFormat("es-CL", {
//     style: "currency",
//     currency: "CLP",
//     minimumFractionDigits: 0,
//     maximumFractionDigits: 0,
//   }).format(saldo);
// };

/**
 * Obtiene el estado del saldo y sus colores correspondientes
 */
const getSaldoStatus = (saldo: number) => {
  if (saldo > 10000) {
    return {
      status: "Alto",
      color: "text-emerald-600",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/20",
      icon: CheckCircle,
      gradient: "from-emerald-500/20 to-emerald-600/10",
      message: "¡Excelente! Tu hijo tiene muchos productos disponibles",
    };
  } else if (saldo > 5000) {
    return {
      status: "Medio",
      color: "text-amber-600",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/20",
      icon: TrendingUp,
      gradient: "from-amber-500/20 to-amber-600/10",
      message: "Buen nivel de productos disponibles",
    };
  } else {
    return {
      status: "Bajo",
      color: "text-rose-600",
      bgColor: "bg-rose-500/10",
      borderColor: "border-rose-500/20",
      icon: AlertCircle,
      gradient: "from-rose-500/20 to-rose-600/10",
      message: "Considera asignar más productos",
    };
  }
};

/**
 * Formatea la fecha
 */
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("es-CL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const ChildCard: React.FC<ChildCardProps> = ({
  student,
  onCargarSaldo,
  onVerConsumos,
  className,
}) => {
  const saldoStatus = getSaldoStatus(student.ticketBalance || 0);
  const StatusIcon = saldoStatus.icon;

  return (
    <Card
      className={`group hover:scale-[1.02] transition-all duration-300 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-2xl ${className}`}
    >
      <CardContent className="p-4 sm:p-6">
        {/* Header con nombre y estado - Mobile Optimized */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
              <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-base sm:text-lg text-foreground group-hover:text-primary transition-colors duration-200 truncate">
                {student.nombre} {student.apellido}
              </h3>
              <p className="text-xs text-muted-foreground">
                Registrado el {formatDate(student.created_at)}
              </p>
            </div>
          </div>
          <Badge
            variant="outline"
            className={`text-xs font-medium ${saldoStatus.borderColor} ${saldoStatus.bgColor}`}
          >
            {saldoStatus.status}
          </Badge>
        </div>

        {/* Información del estudiante - Mobile Optimized */}
        <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-muted/50 rounded-full flex items-center justify-center flex-shrink-0">
              <Mail className="h-3 w-3" />
            </div>
            <span className="truncate">{student.email}</span>
          </div>
          {student.telefono && (
            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-muted/50 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="h-3 w-3" />
              </div>
              <span>{student.telefono}</span>
            </div>
          )}
        </div>

        {/* Productos disponibles con diseño mejorado - Mobile Optimized */}
        <div
          className={`bg-gradient-to-r ${saldoStatus.gradient} border ${saldoStatus.borderColor} rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 group-hover:scale-105 transition-transform duration-200`}
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div
              className={`w-10 h-10 sm:w-12 sm:h-12 ${saldoStatus.bgColor} rounded-full flex items-center justify-center shadow-lg flex-shrink-0`}
            >
              <StatusIcon
                className={`h-5 w-5 sm:h-6 sm:w-6 ${saldoStatus.color}`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-1 sm:gap-2">
                <span
                  className={`font-bold text-xl sm:text-2xl ${saldoStatus.color}`}
                >
                  {student.ticketBalance || 0}
                </span>
                <span className="text-xs sm:text-sm text-muted-foreground">
                  productos
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {saldoStatus.message}
              </p>
            </div>
          </div>
        </div>

        {/* Acciones mejoradas - Mobile Optimized */}
        <div className="flex gap-2 sm:gap-3">
          <Button
            onClick={() => onCargarSaldo(student)}
            size="sm"
            className="flex-1 h-10 sm:h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-lg hover:shadow-xl rounded-xl sm:rounded-2xl group/btn text-xs sm:text-sm"
          >
            <div className="flex items-center gap-1 sm:gap-2">
              <Gift className="h-3 w-3 sm:h-4 sm:w-4 group-hover/btn:scale-110 transition-transform duration-200" />
              <span className="font-medium">Asignar</span>
            </div>
          </Button>
          <Button
            onClick={() => onVerConsumos(student)}
            variant="outline"
            size="sm"
            className="flex-1 h-10 sm:h-12 hover:bg-primary/10 transition-all duration-200 rounded-xl sm:rounded-2xl group/btn text-xs sm:text-sm"
          >
            <div className="flex items-center gap-1 sm:gap-2">
              <Eye className="h-3 w-3 sm:h-4 sm:w-4 group-hover/btn:scale-110 transition-transform duration-200" />
              <span className="font-medium">Historial</span>
            </div>
          </Button>
        </div>

        {/* Indicador de estado emocional - Mobile Optimized */}
        <div className="mt-3 sm:mt-4 flex items-center justify-center">
          <div className="flex items-center gap-1 sm:gap-2 text-xs text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            <span>
              {student.ticketBalance && student.ticketBalance > 10
                ? "¡Todo listo para el día!"
                : "Necesita más productos"}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
